import React, { useEffect, useState } from 'react';
import { string, func, bool, oneOfType } from 'prop-types';
import classNames from 'classnames';

import { useConfiguration } from '../../../context/configurationContext';

import { FormattedMessage, intlShape, injectIntl } from '../../../util/reactIntl';
import { displayPrice } from '../../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../../util/uiHelpers';
import { propTypes } from '../../../util/types';
import { formatMoney } from '../../../util/currency';
import { ensureListing, ensureUser } from '../../../util/data';
import { richText } from '../../../util/richText';
import { createSlug } from '../../../util/urlHelpers';
import { isBookingProcessAlias } from '../../../transactions/transaction';

import { AspectRatioWrapper, IconSpinner, NamedLink, ResponsiveImage } from '../../../components';

import css from './ListingCard.module.css';
import IconHeart from '../../../components/IconHeart/IconHeart';
import { useDispatch, useSelector } from 'react-redux';
import { addToFavorites, likeListing } from '../SearchPage.duck';
import { formatCurrency, getcurrencySymbol } from '../../SubscriptionPlans/SubscriptionHelpers';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = props => {
  const { price, publicData, config, intl } = props;
  const { listingType } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);
  // console.log('price', price?.amount / 100)
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const local = params.get('local');
  return (
    <div className={css.price}>
      <div
        className={classNames(
          css.priceValue,
          local === 'de' ? css.priceValueeuro : css.priceValuedoller
        )}
        title={priceTitle}
      >
        {/* {formattedPrice} */}
        {formatCurrency(price?.amount)}
      </div>
      {isBookable ? (
        <div className={css.perUnit}>
          <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
        </div>
      ) : null}
    </div>
  );
};

export const ListingCardComponent = props => {
  const config = useConfiguration();
  const {
    className,
    rootClassName,
    intl,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData } = currentListing.attributes;
  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;
  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  const currentUser = useSelector(state => state.user.currentUser);

  const [isWhisListActive, setIsWishListActive] = useState(false);

  useEffect(() => {
    const favoritesListing = currentUser?.attributes?.profile?.publicData?.favoritesListing || [];
    const isListingAlreadyExist = favoritesListing.includes(id);
    setIsWishListActive(isListingAlreadyExist);
  }, [currentUser]);

  const dispatch = useDispatch();

  const [showloaderIcon, setLoader] = useState(false);
  const AddToWishList = async event => {
    event.preventDefault();
    // && !isOwnListing

    await dispatch(likeListing(id)).then(res => {
      console.log(res, 'like----');
    });

    if (currentUser?.id?.uuid) {
      setLoader(prev => !prev);
      await dispatch(addToFavorites({ listingId: id, currentUserId: currentUser?.id?.uuid })).then(
        res => {
          setIsWishListActive(res?.isFavorites);
          setLoader(prev => !prev);
        }
      );
    }
  };

  console.log('listingTitle', listing);
  return (
    <NamedLink
      className={classNames(classes, css.linkWrapper)}
      name={
        listing?.attributes?.state === 'pendingApproval' ? 'EditCustomListingPage' : 'ListingPage'
      }
      params={{ id, slug }}
    >
      <div className={css.categoryWrapper}>{listing.attributes.publicData?.businessSegment}</div>
      <div className={css.listingCardWrapper}>
        <AspectRatioWrapper
          className={css.aspectRatioWrapper}
          width={aspectWidth}
          height={aspectHeight}
          {...setActivePropsMaybe}
        >
          <LazyImage
            rootClassName={css.rootForImage}
            alt={title}
            image={firstImage}
            variants={variants}
            sizes={renderSizes}
          />
        </AspectRatioWrapper>
        <div className={css.info}>
          <div className={css.mainInfo}>
            <div className={css.address}>{listing.attributes.publicData.location.address}</div>
            {/* <div className={css.address}>
              {listing.attributes.publicData.location.address}
            </div>
            {/* <div className={css.title}>
              {richText(title, {
                longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                longWordClass: css.longWord,
              })}
            </div> */}
            <div className={css.description}>{listing?.attributes?.title}</div>
            {/* {showAuthorInfo ? (
              <div className={css.authorInfo}>
                <FormattedMessage id="ListingCard.author" values={{ authorName }} />
              </div>
            ) : null} */}
          </div>
          <div className={classNames(css.cardFooterWrapper, 'dFlex')}>
            <PriceMaybe price={price} publicData={publicData} config={config} intl={intl} />
            <div
              className={classNames({
                [css.wishListActive]: isWhisListActive,
                [css.wishList]: !isWhisListActive,
              })}
              onClick={AddToWishList}
            >
              {showloaderIcon ? <IconSpinner className={css.spinnerCustom} /> : <IconHeart />}
            </div>
          </div>
        </div>
      </div>
      {listing?.attributes?.state === 'pendingApproval' && (
        <div className={css.pendingApprovalBg}>{intl.formatMessage({id: "Listing.pendingApproval"})}</div>
      )}
    </NamedLink>
  );
};

ListingCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  setActiveListing: null,
  showAuthorInfo: true,
};

ListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listing: oneOfType([propTypes.listing, propTypes.ownListing]).isRequired,
  showAuthorInfo: bool,

  // Responsive image sizes hint
  renderSizes: string,

  setActiveListing: func,
};

export default injectIntl(ListingCardComponent);
